import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api.js';
import { ALL, type AuditFacets, type AuditResponse, PAGE_SIZE } from './types.js';
import { useDebounced } from './use-debounced.js';

export const useAudit = () => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [actionFilter, setActionFilter] = useState<string>(ALL);
  const [entityFilter, setEntityFilter] = useState<string>(ALL);
  const [actorFilter, setActorFilter] = useState<string>(ALL);
  const [idQuery, setIdQuery] = useState('');
  const debouncedId = useDebounced(idQuery.trim(), 300);

  const facets = useQuery({
    queryKey: ['admin-audit-facets'],
    queryFn: () => api.request<AuditFacets>('/v1/admin/audit/facets'),
  });

  const filters = {
    action: actionFilter !== ALL ? actionFilter : undefined,
    entity: entityFilter !== ALL ? entityFilter : undefined,
    actor: actorFilter !== ALL ? actorFilter : undefined,
    entityId: debouncedId || undefined,
  };

  const audit = useInfiniteQuery({
    queryKey: ['admin-audit', filters],
    queryFn: ({ pageParam }) =>
      api.request<AuditResponse>('/v1/admin/audit', {
        query: {
          limit: PAGE_SIZE,
          ...(pageParam ? { before: pageParam } : {}),
          ...filters,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const entries = audit.data?.pages.flatMap((p) => p.entries) ?? [];

  const clearFilters = () => {
    setActionFilter(ALL);
    setEntityFilter(ALL);
    setActorFilter(ALL);
    setIdQuery('');
  };

  const hasActiveFilter =
    actionFilter !== ALL || entityFilter !== ALL || actorFilter !== ALL || idQuery !== '';

  return {
    facets,
    audit,
    expanded,
    toggle,
    entries,
    actionFilter,
    setActionFilter,
    entityFilter,
    setEntityFilter,
    actorFilter,
    setActorFilter,
    idQuery,
    setIdQuery,
    clearFilters,
    hasActiveFilter,
  };
};
